import { Students } from "classes/models/students/student.model";
import { StudentsService } from "~/classes/models/students/students.service";

export const useStudentsClassroomsData = (userId: string) =>
  defineStore(`/users/${userId}/students/classrooms/data`, () => {
    const appUserDataStore = useAppUserDataStore();
    const { studentPermissions } = storeToRefs(appUserDataStore);

    const activeStudentId = ref<string>();
    const students = ref<Students>([]);
    const selectableStudents = computed(() =>
      students.value.filter((student) => {
        if (student.isArchived) return false;
        const classroomStore = useStudentClassroomDataStore(student.id!);
        // A failed read is not evidence that a classroom was archived. Keep that
        // profile accessible so the portal can show its error and retry action.
        return (
          classroomStore.classrooms.length > 0 ||
          classroomStore.failedClassroomIds.length > 0
        );
      })
    );

    const isInitialized = ref(false);
    let initializationPromise: Promise<void> | undefined;

    const initializeStudentsClassroomData = async () => {
      if (isInitialized.value == true) {
        return;
      }

      if (initializationPromise != undefined) {
        return initializationPromise;
      }

      initializationPromise = (async () => {
        let studentIds = [] as string[];
        const pseduoStudentId = useTeacherPesudoStudentLogin().pseudoStudentId;

        if (pseduoStudentId != undefined) {
          studentIds.push(pseduoStudentId);
        } else {
          studentIds = studentPermissions.value;
        }

        for (const studentId of studentIds) {
          const studentClassroomDataStore =
            useStudentClassroomDataStore(studentId);
          try {
            await studentClassroomDataStore.initializeStudentClassroomData();
          } catch (error) {
            // Remove the student Id from the array
            studentIds = studentIds.filter((id) => id != studentId);
          }
        }

        students.value = [];

        // get each student
        for (const studentId of studentIds) {
          const student = await StudentsService.get(studentId);
          students.value.push(student);
        }

        students.value = students.value.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });

        // Prefer a profile with loaded classrooms over old links or read errors.
        // Retain an identity when all classrooms are archived so the existing
        // empty state can render without redirecting the student to an error.
        activeStudentId.value = selectableStudents.value.find((student) =>
          useStudentClassroomDataStore(student.id!).classrooms.length > 0
        )?.id ?? selectableStudents.value[0]?.id ?? students.value[0]?.id;
        isInitialized.value = true;
      })();

      try {
        await initializationPromise;
      } finally {
        initializationPromise = undefined;
      }
    };

    const changeActiveStudent = (studentId: string) => {
      if (selectableStudents.value.some((student) => student.id === studentId)) {
        activeStudentId.value = studentId;
      }
    };

    return {
      isInitialized,
      initializeStudentsClassroomData,
      changeActiveStudent,
      activeStudentId,
      students,
      selectableStudents,
    };
  });
